// MethodologyStep — method choice + rationale + suitability (F6.1-F6.5). Step 4.

import { Check, X } from 'lucide-react';
import { useActiveValuation, useStore } from '../../store/selectors';
import { Field } from '../common/Field';
import { checkSuitability } from '@backsolve/shared-logic/methodSuitability';

export function MethodologyStep() {
  const active = useActiveValuation();
  const setAssumptions = useStore((s) => s.setAssumptions);
  if (!active) return null;
  const a = active.inputs.assumptions;
  const suitability = checkSuitability(active.inputs);

  return (
    <div className="card">
      <div className="card-title">Methodology</div>
      <Field
        label="Allocation Method"
        htmlFor="method"
        helpText="OPM = option-pricing method with a backsolve + Black-Scholes (default). PWERM = probability-weighted scenarios. Hybrid blends OPM + PWERM. CVM splits value pro-rata with no waterfall."
      >
        <select
          id="method"
          className="select"
          value={a.method}
          onChange={(e) => setAssumptions(active.valuationId, { method: e.target.value as typeof a.method })}
        >
          <option value="opm">OPM (backsolve + Black-Scholes)</option>
          <option value="pwer">PWERM (probability-weighted scenarios)</option>
          <option value="hybrid">Hybrid (OPM/PWERM blend)</option>
          <option value="cvm">Current Value Method</option>
        </select>
      </Field>
      <Field
        label="Method-Selection Rationale"
        htmlFor="rationale"
        hint="Document why this method was chosen (defensibility)"
      >
        <textarea
          id="rationale"
          className="textarea"
          rows={4}
          value={a.methodRationale}
          onChange={(e) => setAssumptions(active.valuationId, { methodRationale: e.target.value })}
        />
      </Field>

      <div className="mt-3">
        <h4 className="card-title" style={{ fontSize: 'var(--font-size-2)' }}>Method Suitability Assessment</h4>
        <table className="data-table">
          <thead>
            <tr>
              <th>Method</th>
              <th>Suitable</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {suitability.flags.map((f) => (
              <tr key={f.method} className={f.method === a.method ? 'row-selected' : ''}>
                <td><strong>{f.method.toUpperCase()}</strong></td>
                <td>{f.suitable ? <span className="badge badge-success"><Check size={12} /></span> : <span className="badge badge-error"><X size={12} /></span>}</td>
                <td style={{ fontSize: 'var(--font-size-1)' }}>{f.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {suitability.needsLattice && (
          <p className="badge badge-warning mt-2">
            Lattice model recommended — path-dependent vesting/anti-dilution features detected.
          </p>
        )}
        {suitability.needsMonteCarlo && (
          <p className="badge badge-warning mt-2">
            Monte-Carlo simulation recommended — complex waterfall with participating preferred + convertibles.
          </p>
        )}
        <p className="field-hint mt-2">
          Recommended method: <strong>{suitability.recommended.toUpperCase()}</strong>
          <button
            type="button"
            className="btn btn-secondary ml-2"
            disabled={a.method === suitability.recommended}
            onClick={() => setAssumptions(active.valuationId, { method: suitability.recommended })}
          >
            Use recommended ({suitability.recommended.toUpperCase()})
          </button>
        </p>
      </div>
    </div>
  );
}
